import { Box, Chip, Typography, Skeleton, Select, MenuItem, FormControl, ToggleButtonGroup, ToggleButton, Pagination } from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import type { ConversationSummary, ConversationFilters } from "../../api/conversations";
import { formatDistanceToNow, parseISO } from "date-fns";

// ─── Sentiment pill config ───────────────────────────────────────────────────

interface PillConfig {
  label: string;
  filter: { sentiment?: string; needs_attention?: boolean; risk_flag?: string };
}

const PILLS: PillConfig[] = [
  { label: "All",                filter: {} },
  { label: "Needs Attention",    filter: { needs_attention: true } },
  { label: "Negative",          filter: { sentiment: "negative" } },
  { label: "Mixed",             filter: { sentiment: "mixed" } },
  { label: "Complaint",         filter: { risk_flag: "complaint" } },
  { label: "Booking Friction",  filter: { risk_flag: "booking_friction" } },
  { label: "Availability Issue",filter: { risk_flag: "availability_issue" } },
  { label: "Pricing Confusion", filter: { risk_flag: "pricing_confusion" } },
  { label: "Lead Capture",      filter: { risk_flag: "lead_capture" } },
  { label: "Handoff Needed",    filter: { risk_flag: "handoff_needed" } },
];

function isSamePill(filters: ConversationFilters, pill: PillConfig): boolean {
  if (!pill.filter.sentiment && !pill.filter.needs_attention && !pill.filter.risk_flag) {
    return !filters.sentiment && !filters.needs_attention && !filters.risk_flag;
  }
  if (pill.filter.needs_attention) return !!filters.needs_attention;
  if (pill.filter.sentiment) return filters.sentiment === pill.filter.sentiment;
  if (pill.filter.risk_flag) return filters.risk_flag === pill.filter.risk_flag;
  return false;
}

// ─── Channel config ───────────────────────────────────────────────────────────

const CHANNEL_COLOURS: Record<string, string> = {
  web_widget: "#C9734F",
  facebook: "#3B82F6",
  instagram: "#A855F7",
  sms: "#D4A63D",
  voice: "#22C55E",
  whatsapp: "#22C55E",
};

function channelColour(ch: string) {
  return CHANNEL_COLOURS[ch] ?? "#64748B";
}

// ─── Outcome chip ─────────────────────────────────────────────────────────────

function OutcomeChip({ outcome }: { outcome: string | null }) {
  if (!outcome) return <span style={{ fontSize: "0.72rem", color: "#94A3B8" }}>No outcome</span>;
  const cfg: Record<string, { color: string; bg: string; label: string }> = {
    booked:    { color: "#16A34A", bg: "#F0FDF4", label: "Booked" },
    escalated: { color: "#D97706", bg: "#FFFBEB", label: "Escalated" },
    lead:      { color: "#3B82F6", bg: "#EFF6FF", label: "Lead" },
  };
  const c = cfg[outcome] ?? { color: "#64748B", bg: "#F8FAFC", label: outcome };
  return (
    <span style={{ fontSize: "0.72rem", fontWeight: 600, color: c.color, backgroundColor: c.bg, borderRadius: 6, padding: "2px 7px" }}>
      {c.label}
    </span>
  );
}

// ─── Sentiment badge ──────────────────────────────────────────────────────────

const SENTIMENT_COLOURS: Record<string, string> = {
  positive: "#16A34A",
  neutral:  "#64748B",
  negative: "#EF4444",
  mixed:    "#D97706",
};

const RISK_FLAG_LABELS: Record<string, string> = {
  complaint:          "Complaint",
  angry_customer:     "Angry",
  cancellation_risk:  "Cancellation Risk",
  pricing_confusion:  "Pricing Confusion",
  booking_friction:   "Booking Friction",
  availability_issue: "Availability Issue",
  service_confusion:  "Service Confusion",
  handoff_needed:     "Handoff Needed",
  repeat_contact:     "Repeat Contact",
  lead_capture:       "Lead Capture",
};

function SentimentBadge({
  label, status, riskFlags,
}: {
  label: string | null;
  status: string;
  riskFlags: string[];
}) {
  if (status !== "completed" || !label) return null;
  const color = SENTIMENT_COLOURS[label.toLowerCase()] ?? "#64748B";
  const topFlag = riskFlags.length > 0 ? RISK_FLAG_LABELS[riskFlags[0]] ?? riskFlags[0] : null;
  const text = topFlag
    ? `${label.charAt(0).toUpperCase() + label.slice(1)} · ${topFlag}`
    : label.charAt(0).toUpperCase() + label.slice(1);
  return (
    <span style={{ fontSize: "0.7rem", color, fontWeight: 600 }}>
      {text}
    </span>
  );
}

// ─── Status dot ───────────────────────────────────────────────────────────────

function StatusDot({ status }: { status: string }) {
  const style: Record<string, { color: string; pulse: boolean }> = {
    active:    { color: "#22C55E", pulse: true },
    closed:    { color: "#CBD5E1", pulse: false },
    abandoned: { color: "#F59E0B", pulse: false },
    escalated: { color: "#EF4444", pulse: false },
  };
  const s = style[status] ?? style.closed;
  return (
    <Box
      sx={{
        width: 8, height: 8, borderRadius: "50%",
        backgroundColor: s.color, flexShrink: 0,
        ...(s.pulse ? {
          boxShadow: `0 0 0 0 ${s.color}`,
          animation: "pulse 1.5s infinite",
          "@keyframes pulse": {
            "0%": { boxShadow: `0 0 0 0 rgba(34,197,94,0.5)` },
            "70%": { boxShadow: `0 0 0 6px rgba(34,197,94,0)` },
            "100%": { boxShadow: `0 0 0 0 rgba(34,197,94,0)` },
          },
        } : {}),
      }}
    />
  );
}

// ─── Relative time ─────────────────────────────────────────────────────────────

function relativeTime(iso: string) {
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

function primaryName(c: ConversationSummary) {
  return c.display?.primary_name || c.customer_name;
}

function hasIdentityMismatch(c: ConversationSummary) {
  return (
    c.identity_resolution_status === "mismatch_flagged" ||
    Boolean(c.mismatch_flags?.length)
  );
}

function matchedContactLabel(c: ConversationSummary) {
  const contactName = c.matched_contact?.display_name;
  const currentName = primaryName(c);
  if (!contactName || contactName === currentName) return null;
  return `Matched contact: ${contactName}`;
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  conversations: ConversationSummary[];
  total: number;
  loading: boolean;
  filters: ConversationFilters;
  onFilterChange: (f: Partial<ConversationFilters>) => void;
  selectedId: string | null;
  onSelect: (c: ConversationSummary) => void;
  page: number;
  onPageChange: (p: number) => void;
  pageSize: number;
}

export default function ConversationList({
  conversations, total, loading, filters, onFilterChange,
  selectedId, onSelect, page, onPageChange, pageSize,
}: Props) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Filters */}
      <Box sx={{ p: 1.5, borderBottom: "1px solid #E2E8F0", flexShrink: 0 }}>
        <ToggleButtonGroup
          value={filters.status || ""}
          exclusive
          onChange={(_, v) => onFilterChange({ status: v || "", offset: 0 })}
          size="small"
          fullWidth
          sx={{
            mb: 1,
            "& .MuiToggleButton-root": {
              border: "1px solid #E2E8F0", color: "#64748B", fontWeight: 600,
              fontSize: "0.75rem", textTransform: "none", py: 0.5,
              "&.Mui-selected": { backgroundColor: "#C9734F", color: "#fff", borderColor: "#C9734F" },
            },
          }}
        >
          <ToggleButton value="">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="closed">Closed</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={filters.channel || ""}
              displayEmpty
              onChange={(e) => onFilterChange({ channel: e.target.value, offset: 0 })}
              sx={{ fontSize: "0.78rem" }}
            >
              <MenuItem value="">All Channels</MenuItem>
              <MenuItem value="web_widget">Web Widget</MenuItem>
              <MenuItem value="facebook">Facebook</MenuItem>
              <MenuItem value="instagram">Instagram</MenuItem>
              <MenuItem value="sms">SMS</MenuItem>
              <MenuItem value="voice">Voice</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={filters.outcome || ""}
              displayEmpty
              onChange={(e) => onFilterChange({ outcome: e.target.value, offset: 0 })}
              sx={{ fontSize: "0.78rem" }}
            >
              <MenuItem value="">All Outcomes</MenuItem>
              <MenuItem value="booked">Booked</MenuItem>
              <MenuItem value="none">No Outcome</MenuItem>
              <MenuItem value="escalated">Escalated</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Sentiment filter pills */}
        <Box
          sx={{
            mt: 1,
            display: "flex",
            gap: 0.75,
            overflowX: "auto",
            pb: 0.5,
            "&::-webkit-scrollbar": { height: 3 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#E2E8F0", borderRadius: 2 },
          }}
        >
          {PILLS.map((pill) => {
            const active = isSamePill(filters, pill);
            return (
              <Box
                key={pill.label}
                onClick={() =>
                  onFilterChange({
                    sentiment: pill.filter.sentiment,
                    needs_attention: pill.filter.needs_attention,
                    risk_flag: pill.filter.risk_flag,
                    offset: 0,
                  })
                }
                sx={{
                  flexShrink: 0,
                  cursor: "pointer",
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: "20px",
                  border: "1px solid",
                  borderColor: active ? "#C9734F" : "#E2E8F0",
                  backgroundColor: active ? "#FEF3EC" : "transparent",
                  color: active ? "#C9734F" : "#64748B",
                  transition: "all 0.15s",
                  "&:hover": { borderColor: "#C9734F", color: "#C9734F" },
                  userSelect: "none",
                  whiteSpace: "nowrap",
                }}
              >
                {pill.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* List */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} sx={{ p: 1.5, borderBottom: "1px solid #F1F5F9" }}>
              <Skeleton width="60%" height={16} />
              <Skeleton width="90%" height={14} sx={{ mt: 0.5 }} />
              <Skeleton width="40%" height={12} sx={{ mt: 0.5 }} />
            </Box>
          ))
        ) : conversations.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 40, color: "#CBD5E1", mb: 1 }} />
            <Typography sx={{ fontSize: "0.85rem", color: "#94A3B8" }}>No conversations found</Typography>
          </Box>
        ) : (
          conversations.map((c) => {
            const isSelected = c.analytics_conversation_id === selectedId;
            return (
              <Box
                key={c.analytics_conversation_id}
                onClick={() => onSelect(c)}
                sx={{
                  px: 1.5, py: 1.2,
                  borderBottom: "1px solid #F1F5F9",
                  borderLeft: isSelected ? "3px solid #C9734F" : "3px solid transparent",
                  backgroundColor: isSelected ? "#FEF3EC" : "transparent",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: isSelected ? "#FEF3EC" : "#F8FAFC" },
                }}
              >
                {/* Row 1: status + name + time */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.4 }}>
                  <StatusDot status={c.status} />
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: channelColour(c.channel), flexShrink: 0 }}
                    title={c.channel_label}
                  />
                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#0F172A", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {primaryName(c)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: "#94A3B8", flexShrink: 0 }}>
                    {relativeTime(c.last_activity_at)}
                  </Typography>
                </Box>
                {/* Row 2: last message */}
                <Typography sx={{ fontSize: "0.77rem", color: c.last_message_role === "assistant" ? "#94A3B8" : "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", pl: 2.2, mb: 0.4 }}>
                  {c.last_message ? (c.last_message_role === "assistant" ? `AI: ${c.last_message}` : c.last_message) : "No messages"}
                </Typography>
                {(matchedContactLabel(c) || hasIdentityMismatch(c)) && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pl: 2.2, mb: 0.4, minHeight: 20 }}>
                    {matchedContactLabel(c) && (
                      <Typography sx={{ fontSize: "0.7rem", color: "#64748B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {matchedContactLabel(c)}
                      </Typography>
                    )}
                    {hasIdentityMismatch(c) && (
                      <Chip
                        label="Name mismatch"
                        size="small"
                        sx={{ height: 18, fontSize: "0.66rem", fontWeight: 700, color: "#B45309", backgroundColor: "#FEF3C7" }}
                      />
                    )}
                  </Box>
                )}
                {/* Row 3: outcome + sentiment */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, pl: 2.2 }}>
                  <OutcomeChip outcome={c.outcome} />
                  <SentimentBadge label={c.sentiment_label} status={c.sentiment_status} riskFlags={c.sentiment_risk_flags ?? []} />
                  <Typography sx={{ fontSize: "0.7rem", color: "#CBD5E1", ml: "auto" }}>
                    {c.message_count} msg
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Pagination */}
      {total > pageSize && (
        <Box sx={{ borderTop: "1px solid #E2E8F0", p: 1, display: "flex", justifyContent: "center", flexShrink: 0 }}>
          <Pagination
            size="small"
            count={Math.ceil(total / pageSize)}
            page={page}
            onChange={(_, p) => onPageChange(p)}
            sx={{ "& .MuiPaginationItem-root": { fontSize: "0.75rem" } }}
          />
        </Box>
      )}
    </Box>
  );
}
